/**
 * UploadProgressInfoComponent.js
 *
 * DESCRIPTION:
 * This component replaces ImageCarouselComponent during upload.
 * Shows useful information and bilingual messages instead of image slideshow.
 *
 * CREATED: 2026-01-05
 * UPDATED: 2026-01-09 - Added i18n translations
 *
 * REQUIREMENTS:
 * - 2.3: Heartbeat for every process step
 * - 2.4: Alternating DE/EN messages every 5 seconds
 * - 2.4: Folder list display with progress
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * FolderProgressItem - Individual folder progress row
 * Displays a folder with its progress percentage and status
 *
 * @param {Object} folder - {path, progress, completed, current}
 */
function FolderProgressItem({ folder }) {
  const { path, progress = 0, completed = false, current = false } = folder;

  const folderName = path.split("/").filter(Boolean).pop() || path;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "8px 12px",
        background: current ? "rgba(43, 188, 217, 0.1)" : "transparent",
        borderRadius: "6px",
        transition: "background 0.3s ease",
      }}
    >
      {completed ? (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="9" fill="#4CAF50" />
          <path d="M5 9L8 12L13 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14.25 14.625H3.75C3.30229 14.625 2.87301 14.4473 2.55673 14.1311C2.24045 13.8148 2.0625 13.3855 2.0625 12.9375V5.0625C2.0625 4.61495 2.24045 4.18573 2.55673 3.86947C2.87301 3.55322 3.30229 3.375 3.75 3.375H7.125C7.36264 3.37519 7.59516 3.44345 7.79529 3.5719C7.99542 3.70035 8.15468 3.88378 8.25375 4.1H14.2538C14.7014 4.10114 15.1305 4.27966 15.4466 4.59612C15.7627 4.91258 15.9404 5.34215 15.9403 5.78985V12.9399C15.9398 13.3872 15.7617 13.8162 15.4456 14.1322C15.1294 14.4483 14.7002 14.6261 14.2528 14.6266L14.25 14.625Z"
            fill={current ? "#2BBCD9" : "#FFC107"}
          />
        </svg>
      )}

      <span
        style={{
          flex: 1,
          color: current ? "#2BBCD9" : "var(--text-primary)",
          fontWeight: current ? 600 : 400,
          fontSize: "14px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {folderName}
      </span>

      {completed ? (
        <span style={{ color: "#4CAF50", fontWeight: 600, fontSize: "13px" }}>✓</span>
      ) : (
        <span
          style={{
            color: current ? "#2BBCD9" : "var(--text-secondary)",
            fontSize: "13px",
            fontWeight: current ? 600 : 400,
          }}
        >
          {Math.round(progress)}%
        </span>
      )}
    </div>
  );
}

/**
 * UploadProgressInfoComponent - Main component
 *
 * @param {Array} folders - Folder list [{path, progress, completed}, ...]
 * @param {string} currentFolder - Currently uploading folder path
 * @param {string} uploadStatus - Current upload status (preparing/encrypting/uploading/completed/idle)
 * @param {string} statusMessage - Custom status message from worker
 */
export default function UploadProgressInfoComponent({
  folders = [],
  currentFolder = "",
  uploadStatus = "idle",
  statusMessage = "",
}) {
  const { t, i18n } = useTranslation();

  const getStatusMessage = (status) => {
    switch (status) {
      case "preparing":
        return t("uploadProgress.status.preparing");
      case "encrypting":
        return t("uploadProgress.status.encrypting");
      case "uploading":
        return t("uploadProgress.status.uploading");
      case "completed":
        return t("uploadProgress.status.completed");
      default:
        return t("uploadProgress.status.idle");
    }
  };

  const currentFolderIndex = folders.findIndex((f) => f.path === currentFolder);

  return (
    <div className="upload-progress-info-panel">
      {uploadStatus !== "idle" && (
        <div className="status-indicator">
          <div className="status-row">
            <div className={`pulse-dot ${uploadStatus === "uploading" ? "uploading" : ""} ${uploadStatus === "completed" ? "completed" : ""}`} />
            <span className="status-text">{getStatusMessage(uploadStatus)}</span>
          </div>
          {statusMessage && <p className="status-message">{statusMessage}</p>}
        </div>
      )}

      <div className="message-panel">
        <h3 className="message-title">{t("uploadProgress.title")}</h3>
        <p className="message-description">{t("uploadProgress.description")}</p>
        <p className="message-tip">💡 {t("uploadProgress.tip")}</p>

        <div className="language-indicator">
          <span className={`dot ${i18n.language === "de" ? "active" : ""}`} />
          <span className={`dot ${i18n.language === "en" ? "active" : ""}`} />
        </div>
      </div>

      {folders.length > 0 && (
        <div className="folder-list-section">
          <div className="folder-list-header">
            <h4>{t("uploadProgress.folderProgress")}</h4>
            <span className="folder-count">{folders.length}</span>
          </div>

          <div
            className="folder-list-scroll"
            ref={(el) => {
              if (el && currentFolderIndex >= 0) {
                const itemHeight = 42;
                const containerHeight = el.clientHeight;
                const scrollTo = currentFolderIndex * itemHeight - containerHeight / 2 + itemHeight / 2;
                el.scrollTop = Math.max(0, scrollTo);
              }
            }}
          >
            {folders.map((folder, idx) => (
              <FolderProgressItem
                key={folder.path}
                folder={{
                  ...folder,
                  current: folder.path === currentFolder,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}

/**
 * Helper function: Create folder list from file list
 * Can be used in upload process for generating folders prop
 *
 * @param {File[]} files - File array with path property
 * @param {Set<string>} completedPaths - Set of completed file paths
 * @param {string} currentFilePath - Currently uploading file path
 * @returns {Array} - Folder list with progress calculations
 */
export function calculateFolderProgress(files, completedPaths = new Set(), currentFilePath = "") {
  const folderStats = new Map();

  for (const file of files) {
    const filePath = file.path || file.name;
    const parts = filePath.split("/").filter(Boolean);

    if (parts.length > 1) {
      const folderPath = parts.slice(0, -1).join("/");

      if (!folderStats.has(folderPath)) {
        folderStats.set(folderPath, { total: 0, completed: 0 });
      }

      const stats = folderStats.get(folderPath);
      stats.total++;
      if (completedPaths.has(filePath)) {
        stats.completed++;
      }
    }
  }

  const currentFolderPath = currentFilePath
    ? currentFilePath.split("/").slice(0, -1).join("/")
    : "";

  return Array.from(folderStats.entries())
    .map(([path, stats]) => ({
      path,
      progress: stats.total > 0 ? (stats.completed / stats.total) * 100 : 0,
      completed: stats.completed === stats.total,
      filesTotal: stats.total,
      filesCompleted: stats.completed,
    }))
    .sort((a, b) => a.path.localeCompare(b.path));
}
